export interface TermsSection {
  title: string;
  content?: string;
  items?: string[];
  subsections?: TermsSection[];
  contactInfo?: {
    email: string;
    website: string;
  };
}

export interface TermsAndConditionsData {
  title: string;
  lastUpdated: string;
  introduction: string;
  sections: TermsSection[];
}

export const termsAndConditionsEn: TermsAndConditionsData = {
  title: "Terms and Conditions – NCircle",
  lastUpdated: "October 20, 2025",
  introduction: "NCircle is a product developed and operated by DeepMinds. These Terms and Conditions (\"Terms\") govern your access to and use of the NCircle mobile application, website, and related digital services (collectively, the \"Platform\") operated by DeepMinds (\"NCircle,\" \"we,\" \"our,\" or \"us\"). By downloading, registering, or using the Platform, you confirm that you have read, understood, and agree to be legally bound by these Terms and all applicable laws and regulations. If you do not agree, you must not use NCircle.",
  sections: [
    {
      title: "1. Introduction",
      content: "These Terms apply to all participants, including:",
      items: [
        "Users: Individuals participating in verified initiatives.",
        "Businesses: Verified local businesses providing rewards or offers.",
        "Enablers: Verified organizations or institutions launching initiatives."
      ]
    },
    {
      title: "2. About NCircle",
      items: [
        "NCircle is a fintech-driven community and sustainability engagement platform connecting individuals, local businesses, and enablers through verified social, GREEN, and community actions.",
        "Users earn NCircle Coins (\"NCoins\") and Points by joining verified initiatives launched by Enablers.",
        "NCoins can be redeemed for rewards, offers from approved Businesses, or used to participate in other verified initiatives.",
        "NCircle currently operates in Madinah Al-Munawara, Saudi Arabia, with plans for expansion."
      ]
    },
    {
      title: "3. Definitions",
      items: [
        "NCoins / NCircle Coins: Non-monetary digital reward units earned through verified actions.",
        "Points: Internal units convertible into NCoins at NCircle-determined rates.",
        "Users: Registered individuals participating in initiatives.",
        "Businesses: Verified local businesses accepting NCoins or providing rewards.",
        "Enablers: Verified organizations or institutions launching initiatives.",
        "Initiatives: Verified campaigns launched by Enablers.",
        "Impact Wallet: Digital wallet showing NCoins balance, earnings, and redemption history."
      ]
    },
    {
      title: "4. Eligibility",
      items: [
        "Users must be 18 years or older residing in Saudi Arabia.",
        "Users aged 16–17 may participate with verified parental or guardian consent.",
        "Only registered users with a verified NCircle account may access the Platform.",
        "All information must be accurate and current."
      ]
    },
    {
      title: "5. Platform Access & License",
      content: "We grant a limited, non-exclusive, non-transferable, revocable license to access and use the Platform solely for:",
      items: [
        "Participation in verified initiatives",
        "Earning and redeeming NCoins",
        "Interacting with approved Partners and Enablers"
      ],
      subsections: [
        {
          title: "You may not:",
          items: [
            "Copy, modify, or distribute Platform software",
            "Reverse engineer the Platform",
            "Use the Platform for unlawful purposes",
            "Permit unauthorized third parties to access your account"
          ]
        }
      ]
    },
    {
      title: "6. Accounts and Security",
      items: [
        "Registration can be via email, mobile number, or approved third-party login (e.g., Apple ID, Google).",
        "Users are responsible for account credentials and all activity under their account.",
        "Unauthorized access or misuse may result in suspension or termination.",
        "Partners and Enablers must maintain accurate information about their business or initiatives."
      ]
    },
    {
      title: "7. NCoins, Points, and Wallet",
      subsections: [
        {
          title: "Earning:",
          items: [
            "Users earn Points and NCoins by participating in verified initiatives or Partner activities."
          ]
        },
        {
          title: "Conversion & Value:",
          items: [
            "Points may convert into NCoins at rates determined by NCircle.",
            "NCoins and Points are non-transferable and have no monetary value outside the Platform."
          ]
        },
        {
          title: "Redemption:",
          items: [
            "NCoins may be redeemed for rewards from Partners or for participation in initiatives, subject to availability and Partner terms."
          ]
        },
        {
          title: "Expiry:",
          items: [
            "NCoins and Points expire after 12 months of inactivity or upon account closure."
          ]
        },
        {
          title: "Wallet Top-Ups & Payouts:",
          items: [
            "Wallets can be topped up via authorized payment methods",
            "Transaction limits, fees, or processing times may apply",
            "Redemptions are subject to KYC/AML verification"
          ]
        }
      ]
    },
    {
      title: "8. Role-Specific Terms",
      subsections: [
        {
          title: "8.1 Users",
          items: [
            "May join but not host initiatives",
            "Must not manipulate verification data or circumvent reward systems",
            "Must not upload illegal or misleading information",
            "Violations may result in suspension or permanent termination"
          ]
        },
        {
          title: "8.2 Businesses",
          items: [
            "Must provide accurate information and authentic rewards",
            "NCircle is not liable for disputes with Users regarding fulfillment, product quality, or service",
            "May terminate participation with 30 days' written notice"
          ]
        },
        {
          title: "8.3 Enablers",
          items: [
            "Must launch verified initiatives and accurately report outcomes",
            "Responsible for verification of user participation",
            "NCircle acts as a digital intermediary and is not liable for fulfillment issues"
          ]
        }
      ]
    },
    {
      title: "9. KYC, AML, and Financial Compliance",
      items: [
        "Identity verification may be required for wallet features or transactions",
        "Failure to provide accurate information may restrict account or wallet functions",
        "All transactions are monitored for fraud prevention"
      ]
    },
    {
      title: "10. Data and Privacy",
      items: [
        "We collect only data necessary to operate accounts, verify initiatives, and deliver rewards",
        "Data is processed according to Saudi PDPL and NCircle's Privacy Policy",
        "Data is never sold or shared outside NCircle's operational framework"
      ]
    },
    {
      title: "11. Intellectual Property",
      items: [
        "All Platform IP belongs to NCircle",
        "Businesses and Enablers retain ownership of their IP",
        "Users cannot upload content or comment; thus no content license is granted"
      ]
    },
    {
      title: "12. Disclaimers and Limitation of Liability",
      items: [
        "Platform and data are provided \"as is\" and \"as available\"",
        "NCircle is not liable for data loss or revenue loss",
        "NCircle is not liable for service interruptions",
        "NCircle is not liable for disputes between Users, Partners, or Enablers",
        "Total liability does not exceed amounts paid for using the Platform (if any)"
      ]
    },
    {
      title: "13. Indemnification",
      content: "Users, Businesses, and Enablers agree to indemnify NCircle from claims arising from misuse of the Platform or breach of these Terms"
    },
    {
      title: "14. Security and Abuse Prevention",
      items: [
        "Unauthorized access, malware, or system exploitation is prohibited",
        "Violations may result in termination and reporting to authorities"
      ]
    },
    {
      title: "15. Termination",
      items: [
        "Accounts may be suspended or terminated for breach, fraud, abuse, inactivity, or regulatory reasons",
        "Unused NCoins and Points are forfeited upon termination"
      ]
    },
    {
      title: "16. Communication & Support",
      items: [
        "NCircle may send notifications via app, email, or SMS regarding updates, rewards, or verification",
        "Support: support@ncircle.ai"
      ]
    },
    {
      title: "17. Accessibility",
      content: "NCircle strives to make the Platform accessible on standard mobile devices. Contact support for assistance if issues arise."
    },
    {
      title: "18. Dispute Resolution",
      items: [
        "Parties should attempt informal resolution first",
        "If unresolved, disputes are subject to the courts of Madinah Al-Munawara, KSA"
      ]
    },
    {
      title: "19. Severability",
      content: "If any provision is deemed unenforceable, remaining provisions remain in effect"
    },
    {
      title: "20. Material Changes",
      content: "Significant updates affecting rights or obligations will be communicated via app or email. Continued use constitutes acceptance of changes"
    },
    {
      title: "21. Apple and Google Clause",
      content: "By downloading NCircle from the App Store or Play Store, you acknowledge Apple and Google are not responsible for maintenance, warranty, or support of the Platform"
    },
    {
      title: "22. Governing Law and Jurisdiction",
      items: [
        "Governed by the laws of Kingdom of Saudi Arabia",
        "Exclusive jurisdiction: courts of Madinah Al-Munawara, KSA"
      ]
    },
    {
      title: "23. Contact Information",
      contactInfo: {
        email: "support@ncircle.ai",
        website: "www.ncircle.ai"
      }
    }
  ]
};

export const termsAndConditionsAr: TermsAndConditionsData = {
  title: "الشروط والأحكام – NCircle",
  lastUpdated: "٢٠ أكتوبر ٢٠٢٥",
  introduction: "تعد منصة NCircle منتجاً تم تطويره وتشغيله بواسطة DeepMinds. تحكم هذه الشروط والأحكام (\"الشروط\") وصولك إلى واستخدامك لتطبيق NCircle للهواتف الذكية وموقعنا الإلكتروني والخدمات الرقمية المرتبطة بهما (يشار إليها مجتمعة بـ \"المنصة\") التي تشغلها DeepMinds (\"NCircle\" أو \"نحن\" أو \"إننا\"). من خلال تنزيل المنصة أو التسجيل فيها أو استخدامها، فإنك تؤكد أنك قرأت وفهمت ووافقت على الالتزام قانونياً بهذه الشروط وكافة الأنظمة واللوائح المعمول بها في المملكة العربية السعودية. إذا كنت لا توافق، يجب عليك عدم استخدام منصة NCircle.",
  sections: [
    {
      title: "١. مقدمة وتطبيق الشروط",
      content: "تنطبق هذه الشروط على جميع المشاركين في المنصة، بما في ذلك:",
      items: [
        "المستخدمون: الأفراد المشاركون في المبادرات المعتمدة.",
        "المنشآت والشركاء: الشركات والمتاجر المحلية المعتمدة التي تقدم المكافآت أو العروض.",
        "الممكّنون والجهات المبادرة: المنظمات أو المؤسسات أو الجهات المعتمدة التي تطلق المبادرات."
      ]
    },
    {
      title: "٢. نبذة عن منصة NCircle",
      items: [
        "منصة NCircle هي منصة تفاعلية تجمع بين التقنية المالية والتنمية المجتمعية والاستدامة، وتربط الأفراد والشركات المحلية والجهات المبادرة من خلال أعمال مجتمعية وبيئية وخضراء موثقة.",
        "يكتسب المستخدمون عملات NCircle الرقمية (\"NCoins\") ونقاط الأثر من خلال الانضمام إلى المبادرات المعتمدة التي يطلقها الممكّنون.",
        "يمكن استبدال NCoins بمكافآت وعروض حصرية من المتاجر المعتمدة، أو استخدامها للمشاركة في مبادرات أخرى معتمدة.",
        "تعمل المنصة حالياً في منطقة المدينة المنورة، المملكة العربية السعودية، مع خطط توسع مستمرة."
      ]
    },
    {
      title: "٣. التعريفات والمصطلحات",
      items: [
        "عملات NCoins: وحدات مكافأة رقمية غير نقدية تُمنح للمستخدمين مقابل مشاركتهم الموثقة في المبادرات.",
        "النقاط: وحدات أثر رقمية داخلية قابلة للتحويل إلى عملات NCoins وفق معدلات تحددها NCircle.",
        "المستخدمون: الأفراد المسجلون المشاركون في المبادرات والأنشطة.",
        "المتاجر والشركاء: المنشآت التجارية المعتمدة التي تقبل مكافآت NCoins أو تقدم خصومات وعروضاً.",
        "الممكّنون: المنظمات والجهات المعتمدة المؤهلة لإطلاق وتمويل المبادرات.",
        "المبادرات: الحملات والبرامج المعتمدة والمطلقة عبر المنصة.",
        "محفظة الأثر: المحفظة الرقمية التي تعرض رصيد NCoins والأرباح وسجل الاسترداد والمعاملات."
      ]
    },
    {
      title: "٤. الأهلية وشروط الاستخدام",
      items: [
        "يجب أن يكون المستخدم في سن 18 عاماً فأكثر ومقيماً في المملكة العربية السعودية.",
        "يجوز للمستخدمين الذين تتراوح أعمارهم بين 16 و17 عاماً المشاركة بموافقة معتمدة من ولي الأمر أو الوصي الشرعي.",
        "يحق فقط للمستخدمين المسجلين الذين لديهم حساب موثق على NCircle الوصول إلى خدمات المنصة.",
        "يجب أن تكون جميع البيانات المدخلة صحيحة ودقيقة ومحدثة باستمرار."
      ]
    },
    {
      title: "٥. ترخيص الوصول إلى المنصة",
      content: "تمنحك NCircle ترخيصاً محدوداً، غير حصري، غير قابل للتحويل، وقابلاً للإلغاء للوصول إلى المنصة واستخدامها حصرياً لأغراض:",
      items: [
        "المشاركة في المبادرات المعتمدة",
        "كسب واستبدال عملات NCoins والنقاط",
        "التفاعل الآمن مع الشركاء والممكّنين المعتمدين"
      ],
      subsections: [
        {
          title: "يحظر عليك ما يلي:",
          items: [
            "نسخ أو تعديل أو توزيع برمجيات المنصة ومحتواها",
            "الهندسة العكسية لأي جزء من المنصة",
            "استخدام المنصة لأي أغراض غير مشروعة أو مخالفة للأنظمة",
            "السماح لأي أطراف غير مصرح لها بالدخول إلى حسابك أو استخدامه"
          ]
        }
      ]
    },
    {
      title: "٦. الحسابات وأمان تسجيل الدخول",
      items: [
        "يتم التسجيل عبر البريد الإلكتروني، أو رقم الجوال، أو عبر خدمات تسجيل الدخول المعتمدة (مثل Apple ID أو Google).",
        "يتحمل المستخدم المسؤولية الكاملة عن سرية بيانات حسابه وكافة الأنشطة الصادرة عنه.",
        "يحق لـ NCircle تجميد أو إغلاق أي حساب في حال الاشتباه في وصول غير مصرح به أو إساءة استخدام.",
        "يلتزم الشركاء والممكّنون بتقديم معلومات دقيقة ومحدثة عن منشآتهم ومبادراتهم."
      ]
    },
    {
      title: "٧. عملات NCoins والنقاط والمحفظة",
      subsections: [
        {
          title: "الاكتساب:",
          items: [
            "يكتسب المستخدمون النقاط والعملات من خلال المشاركة الحقيقية والمحققة في المبادرات المعتمدة."
          ]
        },
        {
          title: "التحويل والقيمة:",
          items: [
            "يتم تحويل النقاط إلى عملات NCoins وفق أسعار الصرف والمعدلات المقررة من المنصة.",
            "عملات NCoins والنقاط غير قابلة للتحويل إلى حسابات أخرى، ولا تحمل أي قيمة نقدية قانونية خارج نطاق المنصة."
          ]
        },
        {
          title: "الاسترداد والمكافآت:",
          items: [
            "يمكن استبدال NCoins بمكافآت وعروض حصرية لدى الشركاء أو للمشاركة في مبادرات مدعومة، رهن توفر العرض وشروط الشريك."
          ]
        },
        {
          title: "الصلاحية وانتهاء العملات:",
          items: [
            "تنتهي صلاحية NCoins والنقاط بعد 12 شهراً من عدم النشاط أو عند إغلاق الحساب."
          ]
        },
        {
          title: "عمليات التعبئة والمدفوعات:",
          items: [
            "يمكن شحن المحافظ عبر وسائل الدفع المعتمدة نظاماً.",
            "قد تنطبق حدود المعاملات أو الرسوم أو فترات المعالجة البنكية.",
            "تخضع أي عمليات دفع أو استرداد مالي لضوابط مكافحة غسل الأموال (AML) والتحقق من الهوية (KYC)."
          ]
        }
      ]
    },
    {
      title: "٨. الشروط الخاصة بكل دور",
      subsections: [
        {
          title: "٨.١ المستخدمون (الأفراد)",
          items: [
            "يحق للأفراد الانضمام إلى المبادرات والمشاركة فيها ولا يحق لهم استضافة مبادرات دون صفة ممكّن.",
            "يُمنع منعاً باتاً التلاعب ببيانات التحقق أو محاولة التحايل على نظام المكافآت.",
            "يُمنع رفع أي بيانات أو صور مضللة أو غير مشروعة.",
            "تؤدي المخالفات إلى تعليق الحساب أو الإلغاء الدائم ومصادرة المكافآت."
          ]
        },
        {
          title: "٨.٢ المنشآت والشركاء التجاريون",
          items: [
            "الالتزام بتقديم عروض ومكافآت حقيقية ودقيقة وفق ما تم الاتفاق عليه.",
            "لا تتحمل NCircle مسؤولية أي خلافات بين المتجر والمستخدم تتعلق بجودة السلع أو الخدمات.",
            "يجوز للمتجر إنهاء مشاركته بإشعار كتابي مسبق مدته 30 يوماً."
          ]
        },
        {
          title: "٨.٣ الممكّنون والجهات المبادرة",
          items: [
            "إطلاق مبادرات حقيقية ذات أثر مجتمعي أو بيئي وتقديم تقارير شفافة عن مخرجاتها.",
            "تحمل المسؤولية الكاملة عن صحة التحقق من مشاركة المستخدمين الميدانية.",
            "تعمل NCircle كوسيط رقمي ولا تتحمل مسؤولية التنفيذ الميداني للمبادرات."
          ]
        }
      ]
    },
    {
      title: "٩. الامتثال المالي والتحقق من الهوية (KYC / AML)",
      items: [
        "قد يُطلب من المستخدمين أو الشركاء تقديم إثباتات هوية وتوثيق رسمي لاستخدام ميزات المحفظة المتقدمة.",
        "يؤدي الامتناع عن تقديم بيانات التحقق إلى تقييد الوصول للمحفظة أو تعليق الخدمات المالية.",
        "تخضع جميع المعاملات على المنصة للمراقبة الصارمة لمنع الاحتيال وضمان الامتثال للأنظمة المصرفية السعودية."
      ]
    },
    {
      title: "١٠. حماية البيانات والخصوصية",
      items: [
        "نجمع فقط البيانات الضرورية لتشغيل الحسابات والتحقق من المبادرات وإيصال المكافآت.",
        "تتم معالجة كافة البيانات وفق نظام حماية البيانات الشخصية السعودي (PDPL) وسياسة الخصوصية الخاصة بنا.",
        "لا نقوم ببيع أو تأجير بياناتك لأي جهات خارجية خارج النطاق التشغيلي لمنصة NCircle."
      ]
    },
    {
      title: "١١. حقوق الملكية الفكرية",
      items: [
        "جميع العلامات التجارية والبرمجيات والمحتوى والتصاميم الخاصة بـ NCircle هي ملك حصري لـ DeepMinds.",
        "يحتفظ الشركاء والممكّنون بملكية علاماتهم التجارية وشعاراتهم الخاصة.",
        "لا يحق للمستخدمين استغلال أو استنساخ أي من عناصر الملكية الفكرية دون موافقة خطية صريحة."
      ]
    },
    {
      title: "١٢. إخلاء المسؤولية وحدودها",
      items: [
        "يتم توفير المنصة وخدماتها على أساس \"كما هي\" و\"كما هي متاحة\" دون أي ضمانات صريحة أو ضمنية.",
        "لا تتحمل NCircle أي مسؤولية عن أي خسائر غير مباشرة أو انقطاع في الخدمة أو فقدان أرباح.",
        "لا تتحمل المنصة المسؤولية عن النزاعات الفردية بين المستخدمين والشركاء والممكّنين.",
        "لا يتجاوز إجمالي مسؤولية NCircle المبالغ الفعلية المدفوعة لاستخدام المنصة (إن وجدت)."
      ]
    },
    {
      title: "١٣. التعويض وإبراء الذمة",
      content: "يوافق المستخدمون والشركاء والممكّنون على تعويض NCircle وإدارتها وموظفيها عن أي مطالبات أو أضرار أو تكاليف تنشأ عن سوء استخدام المنصة أو الإخلال بهذه الشروط أو انتهاك الأنظمة السارية."
    },
    {
      title: "١٤. الأمان ومنع إساءة الاستخدام",
      items: [
        "يُحظر تماماً أي محاولة للوصول غير المصرح به أو نشر برمجيات خبيثة أو استغلال أي ثغرات برمجية.",
        "يتم التعامل مع الانتهاكات الأمنية بحزم عبر الإيقاف الفوري للحساب والإبلاغ لدى الجهات الأمنية والتنظيمية المختصة."
      ]
    },
    {
      title: "١٥. إنهاء الحساب والخدمة",
      items: [
        "يحق لـ NCircle تعليق أو إنهاء الحسابات في حال الإخلال بالشروط أو الاشتباه في الاحتيال أو عدم النشاط لفترات طويلة.",
        "تُلغى أي عملات NCoins أو نقاط غير مستخدمة عند إنهاء الحساب بسبب الإخلال بالشروط."
      ]
    },
    {
      title: "١٦. الإشعارات والتواصل",
      items: [
        "قد ترسل NCircle إشعارات هامة عبر التطبيق، أو البريد الإلكتروني، أو الرسائل النصية بشأن المعاملات والتحديثات.",
        "للتواصل مع فريق الدعم الفني: support@ncircle.ai"
      ]
    },
    {
      title: "١٧. سهولة الوصول",
      content: "تسعى NCircle لضمان سهولة الوصول إلى المنصة عبر مختلف الأجهزة المتوافقة، ويمكن التواصل مع فريق الدعم في حال مواجهة أي صعوبات تقنية."
    },
    {
      title: "١٨. تسوية النزاعات",
      items: [
        "يتم السعي لحل أي نزاع ودياً وبشكل غير رسمي في المقام الأول.",
        "في حال تعذر الحل الودي، تختص المحاكم في منطقة المدينة المنورة بالمملكة العربية السعودية بالفصل في أي نزاع ينشأ عن هذه الشروط."
      ]
    },
    {
      title: "١٩. قابلية الفصل بين البنود",
      content: "إذا تبيّن أن أي بند من هذه الشروط غير قانوني أو غير قابل للتنفيذ، فإن باقي البنود تظل سارية المفعول بكامل قوتها وأثرها القانوني."
    },
    {
      title: "٢٠. التعديلات الجوهرية على الشروط",
      content: "يجوز لنا تحديث هذه الشروط من حين لآخر لمواكبة التغيرات النظامية أو التشغيلية. ويتم إشعار المستخدمين بالتحديثات الجوهرية. ويعد استمرار استخدام المنصة بعد التحديث موافقة ضمنية على الشروط المعدلة."
    },
    {
      title: "٢١. بند متجري Apple وGoogle",
      content: "بتحميلك لتطبيق NCircle من App Store أو Google Play، فإنك تقر بأن شركتي Apple وGoogle ليستا مسؤولتين عن تشغيل أو صيانة أو ضمان المنصة أو محتواها."
    },
    {
      title: "٢٢. القانون الحاكم والاختصاص القضائي",
      items: [
        "تخضع هذه الشروط وتُفسر حصرياً وفقاً للأنظمة واللوائح المعمول بها في المملكة العربية السعودية.",
        "ينعقد الاختصاص القضائي المكاني لمحاكم منطقة المدينة المنورة، المملكة العربية السعودية."
      ]
    },
    {
      title: "٢٣. بيانات التواصل الرسمية",
      contactInfo: {
        email: "support@ncircle.ai",
        website: "www.ncircle.ai"
      }
    }
  ]
};
